import { FormEventHandler, ReactElement, useState } from 'react';
import { DeviceConfigWifi } from '@/api';
import {
    UIFormInput,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISwitch,
    UIText
} from '@/ui';
import { useUpdateDeviceWifiAction } from '../actions';
import { useDevicesTranslation } from '../hooks';
import { PendingConfigNote } from './pending-config-note';

export type WifiModalProps = {
    deviceId: string;
    slot: number;
    current: DeviceConfigWifi | undefined;
    isOpen: boolean;
    onClose: () => void;
};

export function WifiModal(props: WifiModalProps): ReactElement {
    const { isOpen, onClose } = props;
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <FormContent {...props} /> : null}
        </UIModal>
    );
}

function FormContent(props: WifiModalProps): ReactElement {
    const { deviceId, slot, current, onClose } = props;
    const { t } = useDevicesTranslation();
    const { updateDeviceWifi, loading } = useUpdateDeviceWifiAction({
        deviceId
    });

    const [enabled, setEnabled] = useState(current?.enabled ?? true);
    const [ssid, setSsid] = useState(current?.ssid ?? '');
    // The current password is never prefilled; the user must enter a new one.
    const [password, setPassword] = useState('');
    const [showErrors, setShowErrors] = useState(false);

    function ssidError(): string {
        if (ssid.trim().length === 0) {
            return t('shared.fields.ssid.error_required');
        }
        if (ssid.length > 32) {
            return t('shared.fields.ssid.error_too_long');
        }
        return '';
    }

    function passwordError(): string {
        if (password.length === 0) {
            return t('shared.fields.wifi_password.error_required');
        }
        if (password.length > 32) {
            return t('shared.fields.wifi_password.error_too_long');
        }
        return '';
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        setShowErrors(true);

        if (enabled && (ssidError() || passwordError())) {
            return;
        }

        updateDeviceWifi({
            slot,
            data: enabled
                ? {
                      enabled: true,
                      ssid: ssid.trim(),
                      password
                  }
                : { enabled: false, ssid: null, password: null }
        })
            .then(() => onClose())
            .catch(() => {}); // Error handled in the action
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UIModalHeader
                title={t('components.wifi_modal.title', { number: slot + 1 })}
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <PendingConfigNote />

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t('shared.devices.wifi_enabled_label')}
                            </UIText>
                            <UIText type="default2">
                                {t('components.wifi_modal.enabled_description')}
                            </UIText>
                        </div>
                        <UISwitch checked={enabled} onChange={setEnabled} />
                    </div>

                    {enabled && (
                        <>
                            <UIFormInput
                                label={t('shared.fields.ssid.label')}
                                description={t(
                                    'shared.fields.ssid.description'
                                )}
                                value={ssid}
                                maxLength={32}
                                onChange={setSsid}
                                errorText={showErrors ? ssidError() : ''}
                            />
                            <UIFormInput
                                label={t('shared.fields.wifi_password.label')}
                                description={t(
                                    'shared.fields.wifi_password.description'
                                )}
                                value={password}
                                maxLength={32}
                                onChange={setPassword}
                                errorText={showErrors ? passwordError() : ''}
                            />
                        </>
                    )}
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: t('shared.labels.submit')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </form>
    );
}
